import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface Notification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  message: string
  duration?: number
}

interface Toast {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  message: string
  duration?: number
}

interface Modal {
  isOpen: boolean
  type: string | null
  data: unknown
}

interface SidebarState {
  isOpen: boolean
  activeItem: string
}

interface UIState {
  theme: 'light' | 'dark' | 'system'
  sidebar: SidebarState
  notifications: Notification[]
  toasts: Toast[]
  modal: Modal
  loading: {
    global: boolean
    actions: Record<string, boolean>
  }
  breadcrumbs: Array<{ label: string; path?: string }>
}

const initialState: UIState = {
  theme: 'system',
  sidebar: {
    isOpen: true,
    activeItem: '',
  },
  notifications: [],
  toasts: [],
  modal: {
    isOpen: false,
    type: null,
    data: null,
  },
  loading: {
    global: false,
    actions: {},
  },
  breadcrumbs: [],
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Theme
    setTheme: (state, action: PayloadAction<'light' | 'dark' | 'system'>) => {
      state.theme = action.payload
    },
    toggleTheme: (state) => {
      const themes: Array<'light' | 'dark' | 'system'> = ['light', 'dark', 'system']
      const currentIndex = themes.indexOf(state.theme)
      state.theme = themes[(currentIndex + 1) % themes.length]
    },

    // Sidebar
    toggleSidebar: (state) => {
      state.sidebar.isOpen = !state.sidebar.isOpen
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebar.isOpen = action.payload
    },
    setActiveSidebarItem: (state, action: PayloadAction<string>) => {
      state.sidebar.activeItem = action.payload
    },

    // Notifications
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id'>>) => {
      const id = Math.random().toString(36).substr(2, 9)
      state.notifications.push({
        ...action.payload,
        id,
        duration: action.payload.duration || 5000,
      })
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter((n) => n.id !== action.payload)
    },
    clearNotifications: (state) => {
      state.notifications = []
    },

    // Toasts
    addToast: (state, action: PayloadAction<Omit<Toast, 'id'>>) => {
      const id = Math.random().toString(36).substr(2, 9)
      state.toasts.push({
        ...action.payload,
        id,
        duration: action.payload.duration || 5000,
      })
    },
    removeToast: (state, action: PayloadAction<string>) => {
      state.toasts = state.toasts.filter((t) => t.id !== action.payload)
    },
    clearToasts: (state) => {
      state.toasts = []
    },

    // Modal
    openModal: (state, action: PayloadAction<{ type: string; data?: unknown }>) => {
      state.modal = {
        isOpen: true,
        type: action.payload.type,
        data: action.payload.data || null,
      }
    },
    closeModal: (state) => {
      state.modal = {
        isOpen: false,
        type: null,
        data: null,
      }
    },

    // Loading
    setGlobalLoading: (state, action: PayloadAction<boolean>) => {
      state.loading.global = action.payload
    },
    setActionLoading: (state, action: PayloadAction<{ action: string; loading: boolean }>) => {
      state.loading.actions[action.payload.action] = action.payload.loading
    },

    // Breadcrumbs
    setBreadcrumbs: (state, action: PayloadAction<Array<{ label: string; path?: string }>>) => {
      state.breadcrumbs = action.payload
    },
    addBreadcrumb: (state, action: PayloadAction<{ label: string; path?: string }>) => {
      state.breadcrumbs.push(action.payload)
    },
    clearBreadcrumbs: (state) => {
      state.breadcrumbs = []
    },
  },
})

export const {
  setTheme,
  toggleTheme,
  toggleSidebar,
  setSidebarOpen,
  setActiveSidebarItem,
  addNotification,
  removeNotification,
  clearNotifications,
  addToast,
  removeToast,
  clearToasts,
  openModal,
  closeModal,
  setGlobalLoading,
  setActionLoading,
  setBreadcrumbs,
  addBreadcrumb,
  clearBreadcrumbs,
} = uiSlice.actions

export default uiSlice.reducer
